import { BRAND } from "themes";

export const styles = {
  page: {
    width: "100%",
    maxWidth: 1080,
    margin: "0 auto",
    paddingBottom: "56px",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    marginBottom: "28px",
  },
  backBtn: {
    mt: "2px",
    color: BRAND.primaryDark,
    backgroundColor: BRAND.track,
    borderRadius: "10px",
    width: 40,
    height: 40,
    "&:hover": {
      backgroundColor: "#EDE9F8",
    },
  },
  heading: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.25,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    color: BRAND.muted,
    marginTop: "4px",
    fontSize: "0.875rem",
    lineHeight: 1.45,
    maxWidth: 520,
  },
  section: {
    padding: { xs: "20px", md: "28px 32px" },
    marginBottom: "20px",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "18px",
    bgcolor: "#FFF",
    boxShadow: "0 1px 3px rgba(27, 19, 64, 0.04)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "24px",
  },
  sectionHeaderLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    minWidth: 0,
  },
  sectionIndex: {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: "9px",
    bgcolor: BRAND.track,
    color: BRAND.primaryDark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 700,
    mt: "1px",
  },
  sectionTitle: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: 1.35,
    marginBottom: "4px",
  },
  sectionHint: {
    color: BRAND.muted,
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    fontWeight: 400,
    marginBottom: 0,
  },
  sectionCount: {
    flexShrink: 0,
    px: 1.25,
    py: 0.5,
    borderRadius: "999px",
    bgcolor: BRAND.track,
    color: BRAND.primaryDark,
    fontSize: "0.75rem",
    fontWeight: 700,
    lineHeight: 1.4,
    alignSelf: "flex-start",
    mt: "4px",
  },

  // —— Basic details ——
  basicsLayout: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "163px minmax(0, 1fr)",
    },
    gap: { xs: 3, md: 4 },
    alignItems: "start",
  },
  coverPanel: {
    position: "relative",
    width: 163,
    maxWidth: 163,
  },
  coverDropzone: {
    width: 163,
    height: 229,
    minWidth: 163,
    minHeight: 229,
    display: "flex",
    borderRadius: "12px",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  coverCaption: {
    mt: 1,
    color: BRAND.muted,
    fontSize: "0.75rem",
    lineHeight: 1.45,
    textAlign: "left",
  },
  coverMobileHint: {
    mt: 0.5,
    color: BRAND.primaryDark,
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.45,
    textAlign: "left",
  },
  basicsFields: {
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: 0,
  },
  basicsRow: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
    },
    gap: 2,
  },
  fieldLabel: {
    display: "block",
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "0.8125rem",
    marginBottom: "8px",
  },

  // —— Title / description list ——
  listRoot: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
  },
  composer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: { xs: 2, sm: 2.5 },
    borderRadius: "14px",
    border: `1px solid ${BRAND.border}`,
    bgcolor: "#FAFAFC",
  },
  composerGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
    },
    gap: 2,
    alignItems: "flex-start",
  },
  composerActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  composerButton: {
    height: 42,
    minWidth: 128,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
  },
  listStack: {
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
  },
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1.5,
    p: 2,
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    bgcolor: "#FFF",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: "#D9D4E8",
      boxShadow: "0 2px 8px rgba(27, 19, 64, 0.04)",
    },
  },
  listItemIndex: {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: "8px",
    bgcolor: BRAND.primaryDark,
    color: "#FFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 700,
    mt: "2px",
  },
  listItemBody: {
    flex: 1,
    minWidth: 0,
  },
  listItemTitle: {
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.4,
    wordBreak: "break-word",
  },
  listItemDesc: {
    mt: 0.5,
    color: BRAND.muted,
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  listItemDelete: {
    color: BRAND.muted,
    "&:hover": {
      color: "#D32F2F",
      bgcolor: "#FFF5F5",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    py: 4,
    px: 2,
    borderRadius: "14px",
    border: `1px dashed ${BRAND.border}`,
    bgcolor: "#FAFAFC",
    textAlign: "center",
  },
  emptyStateTitle: {
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  emptyStateHint: {
    color: BRAND.muted,
    fontSize: "0.8125rem",
    maxWidth: 320,
  },

  // —— Media / tools list ——
  mediaRoot: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
  },
  mediaComposer: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "96px minmax(0, 1fr)",
    },
    gap: 2,
    alignItems: "center",
    p: { xs: 2, sm: 2.5 },
    borderRadius: "14px",
    border: `1px solid ${BRAND.border}`,
    bgcolor: "#FAFAFC",
  },
  mediaComposerThumb: {
    position: "relative",
    width: { xs: "100%", sm: 96 },
    maxWidth: { xs: 140, sm: 96 },
    aspectRatio: "1 / 1",
    mx: { xs: "auto", sm: 0 },
    display: "flex",
  },
  mediaComposerFields: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: 1.5,
    minWidth: 0,
  },
  mediaComposerInput: {
    flex: 1,
    height: 48,
    fontSize: "0.875rem",
    borderRadius: "10px",
    bgcolor: "#FFF",
    "& fieldset": {
      borderRadius: "10px",
      borderColor: BRAND.border,
    },
  },
  mediaComposerButton: {
    height: 48,
    minWidth: 120,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    flexShrink: 0,
  },
  mediaList: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
    },
    gap: 1.5,
  },
  mediaItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 1.5,
    pr: 5,
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    bgcolor: "#FFF",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: "#D9D4E8",
      boxShadow: "0 2px 8px rgba(27, 19, 64, 0.04)",
    },
  },
  mediaItemThumb: {
    width: 56,
    height: 56,
    minWidth: 56,
    borderRadius: "10px",
    overflow: "hidden",
    display: "flex",
    flexShrink: 0,
    cursor: "pointer",
    border: `1px solid ${BRAND.border}`,
    bgcolor: "#F8F6FC",
    "& > *": {
      width: "100% !important",
      height: "100% !important",
      minWidth: "100% !important",
      minHeight: "100% !important",
      border: "none",
      borderRadius: "9px",
    },
  },
  mediaItemTitle: {
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.4,
    wordBreak: "break-word",
    minWidth: 0,
  },
  mediaItemMeta: {
    color: BRAND.muted,
    fontSize: "0.75rem",
    mt: 0.25,
  },
  mediaItemDelete: {
    position: "absolute",
    top: 8,
    right: 8,
    color: BRAND.muted,
    "&:hover": {
      color: "#D32F2F",
      bgcolor: "#FFF5F5",
    },
  },

  // —— Shared form controls ——
  field: {
    marginBottom: "16px",
    width: "100%",
  },
  mediaLabel: {
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: "15px",
    marginBottom: "2px",
  },
  mediaHint: {
    color: BRAND.muted,
    display: "block",
    marginBottom: "12px",
  },
  mediaBlock: {
    marginBottom: 0,
  },
  mediaCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    bgcolor: "transparent",
    borderRadius: "12px",
    padding: 0,
    minWidth: 0,
  },
  mediaCardImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    height: "auto",
    minHeight: 140,
    maxWidth: 220,
    display: "flex",
    mb: 1,
  },
  mediaCardTitle: {
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "13px",
    textAlign: "center",
    lineHeight: 1.3,
    px: 0.5,
  },
  mediaCardDelete: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1,
    bgcolor: "#FFF",
    boxShadow: "0 1px 4px rgba(27, 19, 64, 0.12)",
    "&:hover": {
      bgcolor: "#F3EEFF",
    },
  },
  mediaCardInput: {
    "& input": {
      fontSize: "13px",
      textAlign: "center",
      padding: "8px 10px",
    },
  },
  titleDescRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 1.5,
  },
  titleDescAddCard: {
    flex: "1 1 100%",
    width: "100%",
    maxWidth: "100%",
    padding: "16px",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    bgcolor: "#FAFAFC",
  },
  titleDescAddFields: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "minmax(0, 1fr) minmax(0, 1.2fr)",
    },
    gap: 2,
    width: "100%",
    alignItems: "flex-start",
  },
  titleDescAddActions: {
    display: "flex",
    justifyContent: "flex-end",
    mt: 2,
  },
  titleDescAddButton: {
    height: "44px",
    minWidth: "120px",
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  },
  titleDescTextarea: {
    fontSize: "0.875rem",
    color: "#364152",
    fontWeight: 400,
    borderRadius: "10px",
    bgcolor: "#FFF",
    "& textarea": {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },
    "& fieldset": {
      borderRadius: "10px",
      borderColor: BRAND.border,
    },
  },
  titleDescItemCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 1.25,
    px: 1.5,
    pr: 5,
    borderRadius: "12px",
    bgcolor: "#F8F6FC",
    border: `1px solid ${BRAND.border}`,
    width: "fit-content",
    maxWidth: 320,
    minWidth: 160,
    position: "relative",
  },
  titleDescItemText: {
    minWidth: 0,
    pr: 1,
  },
  stepIndex: {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: "50%",
    bgcolor: BRAND.primaryDark,
    color: "#FFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 700,
  },
  selectDropdown: {
    height: "48px",
    fontSize: "0.875rem",
    color: "#364152",
    fontWeight: 400,
    borderRadius: "10px",
    bgcolor: "#FFF",
    "& > div": {
      background: "none",
      color: BRAND.ink,
      borderRadius: "10px",
      fontSize: "0.875rem",
      fontWeight: 400,
    },
    "& fieldset": {
      borderRadius: "10px",
      borderColor: BRAND.border,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "4px",
    padding: "16px 20px",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    bgcolor: "#FFF",
    boxShadow: "0 1px 3px rgba(27, 19, 64, 0.04)",
    position: "sticky",
    bottom: 16,
    zIndex: 2,
  },
  cancelBtn: {
    minWidth: 110,
    height: 44,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  },
  submitBtn: {
    minWidth: 160,
    height: 44,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  },
};
